import * as yup from "yup";
import { Form } from "@/components/organisms/Form";
import { request } from "@/common/api";
import { useForm } from "react-hook-form";
import { Buttons } from "@/components/atoms/Buttons";
import { AlertText } from "@/components/atoms/AlertText";
import { showAlert } from "@/components/containers/Alert";
import { agreeState } from "@/common/states/agree";
import { InputGroup } from "@/components/molecules/InputGroup";
import { TelControl } from "@/components/containers/Controls/TelControl";
import { useNavigate } from "react-router";
import { useMutation } from "@tanstack/react-query";
import { yupResolver } from "@hookform/resolvers/yup";
import { TextControl } from "@/components/containers/Controls/TextControl";
import { BaseResponse } from "@/common/types/baseRespones";
import { FormInputBox } from "@/components/molecules/FormInputBox";
import { JoinTemplate } from "@/components/templates/JoinTemplate";
import { RadioControl } from "@/components/containers/Controls/RadioControl";
import { ModalGuardian } from "@/components/containers/ModalContainer/ModalGuardian";
import { useRecoilValue } from "recoil";
import { JoinProcessBar } from "@/components/atoms/JoinProcessBar";
import { guardianUserState } from "@/common/states/user";
import { useEffect, useState } from "react";
import { useJoinFormHandlers } from "@/common/hooks/useJoinFormHandlers";
import {
  GuardianState,
  joinCommState,
  StudentState,
} from "@/common/states/recoilInfo";

const schema = yup.object({
  guardianExist: yup
    .string()
    .required("법정보호자 계정 여부는 필수 선택입니다."),
  name: yup
    .string()
    .nullable()
    .when("guardianExist", {
      is: "1",
      then: (schema) => schema.required("보호자 이름은 필수 입력입니다."),
      otherwise: (schema) => schema.notRequired(),
    }),
  birth: yup.mixed<Date | string>().when("guardianExist", {
    is: "1",
    then: (schema) => schema.required("생년월일은 필수 입력입니다."),
    otherwise: (schema) => schema.notRequired(),
  }),
  tel: yup
    .string()
    .nullable()
    .when("guardianExist", {
      is: "1",
      then: (schema) => schema.required("연락처는 필수 입력입니다."),
      otherwise: (schema) => schema.notRequired(),
    }),
  agreementTime1: yup
    .date()
    .required("서비스 이용 안내 동의 시간은 필수 입력입니다.")
    .typeError("동의 시간 1은 유효한 날짜여야 합니다."),
  agreementTime2: yup
    .date()
    .required("개인정보 수집∙이용 및 제3자 제공 동의 시간은 필수 입력입니다.")
    .typeError("동의 시간 2는 유효한 날짜여야 합니다."),
});

export const JoinGuardianConnectPage = () => {
  const navigate = useNavigate();
  const agree = useRecoilValue(agreeState);
  const userInfo = useRecoilValue(joinCommState);
  const guardianInfo = useRecoilValue(guardianUserState);
  const { guardianExist, guardianExistRecoil, handleGuardianExistChange } =
    useJoinFormHandlers();
  const [modalShow, setModalShow] = useState(false);

  const { control, handleSubmit, setValue } = useForm({
    resolver: yupResolver(schema),
    defaultValues: {
      tel: guardianInfo.tel || null,
      name: guardianInfo.name || null,
      birth: guardianInfo.birth || new Date(),
      agreementTime1: agree?.agreementTime1!,
      agreementTime2: agree?.agreementTime2!,
    },
  });

  const { mutateAsync: registerUser } = useMutation({
    mutationFn: async (data: {
      student: StudentState;
      guardian: GuardianState;
    }) => {
      return await request<BaseResponse>({
        method: "POST",
        url: "/student/join",
        data: data,
      });
    },
    onError: (error) => {
      showAlert("회원가입 중 오류가 발생하여 실패.");
    },
  });

  const onSubmit = async (data: any) => {
    const studentData: StudentState = {
      tel: guardianExistRecoil.tel,
      name: guardianExistRecoil.name,
      birth: guardianExistRecoil.birth
        ? new Date(guardianExistRecoil.birth)
        : undefined,
      gender: guardianExistRecoil.gender,
      userId: userInfo.userId,
      password: userInfo.password,
      accountFindQuestion: userInfo.accountFindQuestion,
      questionAnswer: userInfo.questionAnswer,
      schoolId: guardianExistRecoil.schoolId,
      schoolType: guardianExistRecoil.schoolType,
      entityType: userInfo.entityType,
      agreementTime1: agree?.agreementTime1!.toISOString(),
      agreementTime2: agree?.agreementTime2!.toISOString(),
      studentIdNumber: guardianExistRecoil.studentIdNumber,
      organizationName: userInfo.organizationName,
      guardianAccExist: guardianExistRecoil.guardianAccExist,
    };

    const guardianData: GuardianState = {
      id: guardianInfo.id,
      tel: guardianInfo.tel,
      name: guardianInfo.name,
      birth: guardianInfo.birth,
    };

    try {
      await registerUser({
        student: studentData,
        guardian: guardianData,
      });
      navigate("/auth/join/done");
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    setValue("tel", guardianInfo.tel);
    setValue("name", guardianInfo.name);
    setValue("agreementTime1", agree?.agreementTime1!);
    setValue("agreementTime2", agree?.agreementTime2!);
    setValue("birth", guardianInfo.birth || new Date());
  }, [guardianInfo, agree, setValue]);

  return (
    <>
      <JoinTemplate>
        <Form
          processBar={<JoinProcessBar stepNow={6} stepTotal={6} />}
          title="법정보호자 연결"
          text={
            <>
              법정보호자 계정이 있으신 경우,
              <br />
              법정보호자 UID를 통해 학생-보호자 연결이 가능합니다.
              <div style={{ color: "red", fontSize: "1.2rem" }}>
                * UID: 법정보호자 계정 로그인 하면, 메인 페이지 우측 상단에 확인
                가능
              </div>
            </>
          }
          submitBtn={
            <Buttons
              type="submit"
              size="lg"
              color="primary100"
              layout="solid"
              label="회원가입"
              disabled={false}
              onClick={handleSubmit(onSubmit)}
            />
          }
          backBtn={
            <Buttons
              type="button"
              size="lg"
              color="primary100"
              layout="text"
              label="뒤로"
              onClick={() => navigate("/auth/join/student-form")}
            />
          }
        >
          <>
            <FormInputBox>
              <>
                <RadioControl
                  size="md"
                  control={control}
                  name="guardianExist"
                  label="법정보호자 계정이 있으신가요?"
                  data={[
                    { value: "1", displayExpr: "예" },
                    { value: "2", displayExpr: "아니오" },
                  ]}
                  required={true}
                  defaultValue={guardianExistRecoil.guardianAccExist}
                  onChange={handleGuardianExistChange}
                />

                {guardianExist === "1" && (
                  <>
                    <InputGroup direction="row" gridColumn="80% 20%">
                      <>
                        <TextControl
                          control={control}
                          name="uid"
                          label="법정보호자 UID"
                          placeholder="UID를 검색해 주세요."
                          disabled={true}
                          required={true}
                        />
                        <Buttons
                          type="button"
                          size="form"
                          layout="solid"
                          color="primary100"
                          label="검색"
                          onClick={() => setModalShow(true)}
                        />
                      </>
                    </InputGroup>
                    <TextControl
                      control={control}
                      name="name"
                      label="법정보호자 이름"
                      disabled={true}
                      value={guardianInfo.name}
                    />
                    <TextControl
                      type="date"
                      control={control}
                      name="birth"
                      label="법정보호자 생년월일"
                      placeholder="0000-00-00"
                      disabled={true}
                      value={
                        guardianInfo.birth
                          ? new Date(guardianInfo.birth)
                              .toISOString()
                              .split("T")[0]
                          : ""
                      }
                    />
                    <TelControl
                      type="tel"
                      control={control}
                      name="tel"
                      label="법정보호자 연락처"
                      placeholder="000-0000-0000"
                      required={true}
                      disabled={true}
                      value={guardianInfo.tel}
                    />
                  </>
                )}

                {guardianExist === "2" && (
                  <AlertText
                    subText={
                      <>
                        추후 '설정 &gt; 법정보호자 연결 설정' 메뉴에서
                        <br />
                        학생-보호자 연결이 가능합니다.
                      </>
                    }
                  />
                )}
              </>
            </FormInputBox>
          </>
        </Form>
      </JoinTemplate>
      {modalShow && <ModalGuardian setModalShow={setModalShow} />}
    </>
  );
};
